import React, { Ref, useMemo, useRef } from 'react';

import classNames from 'classnames';

import { ListItem, ListItemProps } from '@ast/magma/components/list';
import { TextItemProps } from '@ast/magma/components/list/textitem/textitem';
import { Toggle } from '@ast/magma/components/toggle';

import ActionUtils from '@ast/magma/inner/utils/action';

import { isTruthy } from '@app/common/utils';

import styles from './ActionListItem.pcss';

export interface ActionListItemProps {
  readonly checked?: boolean;
  readonly clickable?: boolean;
  readonly description?: React.ReactNode;
  readonly onChange?: React.ChangeEventHandler<HTMLInputElement>;
}

export interface ActionListItemChildRef {
  onClick: () => void;
}

export const ActionListItem = React.forwardRef((
  props: ActionListItemProps & ListItemProps & TextItemProps,
  ref: Ref<HTMLLIElement>,
) => {
  const {
    data,
    description,
    checked,
    clickable = true,
    children,
    className,
    getItemIcon,
    onChange,
    onClick,
    ...otherListItemProps
  } = props;

  const childRef = useRef<ActionListItemChildRef>(null);
  const disabled = isTruthy(props['aria-disabled']);
  const icon = useMemo(() => {
    const component = getItemIcon && getItemIcon(data);

    return component
      ? React.cloneElement(component, {
        className: styles.icon,
      })
      : null;
  }, [getItemIcon, data]);

  const handleClick = (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
    if (!disabled) {
      onClick?.(e);
      childRef.current?.onClick();
    }
  };

  return (
    <ListItem
      ref={ref}
      className={classNames(styles.listItem, className)}
      {...(clickable ? ActionUtils.handleSelect(handleClick) : {})}
      {...otherListItemProps}
    >
      <div
        aria-disabled={disabled}
        className={styles.container}
      >
        {icon && (
          <div data-stable-name="ActionListItemIcon">
            {icon}
          </div>
        )}
        <div className={styles.content}>
          <div className={styles.text}>{data}</div>
          <div className={styles.description}>{description}</div>
          {React.isValidElement(children) && React.cloneElement(children, {
            ref: childRef,
          })}
        </div>
        {onChange && (
          <div className={styles.toggle}>
            <Toggle
              data-stable-name="ActionListItemToggle"
              checked={checked}
              disabled={disabled}
              onChange={onChange}
            />
          </div>
        )}
      </div>
    </ListItem>
  );
});
